import { useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import { PageAtmosphere } from '../components/design-system/PageAtmosphere';
import { captureUTMParams, trackEvent } from '../lib/analytics';
import { getPublicStoreLinks, hasPublicStoreLinks } from '../lib/mobileStoreLinks.js';
import {
  EROICA_OFFICIAL_ROUTES_URL,
  EROICA_WEEKEND_LABEL,
  formatEroicaRaceDayLabel,
  groupEroicaRoutesByRaceDay,
} from '../../shared/eroicaRoutes.js';
import { WWW_PAGE_SHELL_MIN_H_CLASS } from '../lib/wwwViewportShell';
import { MARKETING_NAV_CLASS } from '../components/marketing/MarketingNav';

const CAMPAIGN_UTM_DEFAULTS = Object.freeze({
  utm_source: 'flyer',
  utm_medium: 'print',
  utm_campaign: 'eroica-2026',
});

function useCampaignHref(basePath) {
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(CAMPAIGN_UTM_DEFAULTS)) {
      if (!params.has(key)) {
        params.set(key, value);
      }
    }
    const query = params.toString();
    return query ? `${basePath}?${query}` : basePath;
  }, [basePath, location.search]);
}

function formatDistance(km) {
  return Number.isInteger(km) ? String(km) : km.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function formatElevation(meters) {
  return `${meters.toLocaleString('en-US')} m`;
}

export default function EroicaCampaign() {
  const registerHref = useCampaignHref('/register');
  const signInHref = useCampaignHref('/login');
  const raceDayGroups = groupEroicaRoutesByRaceDay();
  const storeLinks = getPublicStoreLinks();
  const showStores = hasPublicStoreLinks(storeLinks);

  useEffect(() => {
    const utm = captureUTMParams();
    trackEvent('eroica_campaign_view', {
      ...utm,
      timestamp: new Date().toISOString(),
    });
  }, []);

  const handleRegisterClick = useCallback(() => {
    trackEvent('eroica_campaign_cta_click', {
      cta: 'register',
      destination: registerHref,
    });
  }, [registerHref]);

  const handleSignInClick = useCallback(() => {
    trackEvent('eroica_campaign_cta_click', {
      cta: 'sign_in',
      destination: signInHref,
    });
  }, [signInHref]);

  const handleAppStoreClick = useCallback(() => {
    if (!storeLinks.apple) return;
    trackEvent('eroica_campaign_cta_click', {
      cta: 'app_store',
      destination: storeLinks.apple,
    });
  }, [storeLinks.apple]);

  const handlePlayStoreClick = useCallback(() => {
    if (!storeLinks.play) return;
    trackEvent('eroica_campaign_cta_click', {
      cta: 'play_store',
      destination: storeLinks.play,
    });
  }, [storeLinks.play]);

  return (
    <div className={`relative ${WWW_PAGE_SHELL_MIN_H_CLASS} bg-[var(--bg-primary)]`}>
      <PageAtmosphere />

      <nav className={MARKETING_NAV_CLASS}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2">
              <span className="dark:hidden">
                <Logo size={48} showText textClass="text-xl sm:text-2xl" />
              </span>
              <span className="hidden dark:inline-flex">
                <Logo size={48} variant="dark" showText textClass="text-xl sm:text-2xl" />
              </span>
            </Link>
            <ThemeToggle className="text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white" />
          </div>
        </div>
      </nav>

      <main className="relative pt-20 sm:pt-28 pb-16 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center space-y-6 sm:space-y-8">
          <h1 className="font-display text-[clamp(1.75rem,5vw+1rem,3rem)] font-extrabold text-stone-900 dark:text-white leading-tight tracking-tight">
            Under-trained or under-fueled for your route?
            <span className="block text-primary-500 mt-2">We have you covered.</span>
          </h1>
          <p className="text-base sm:text-lg text-stone-600 dark:text-stone-400 leading-relaxed max-w-2xl mx-auto">
            Five race-day routes from Gaiole in Chianti across the {EROICA_WEEKEND_LABEL} weekend.
            Saturday is L'Eroico and Crete Senesi. Sunday is Val d'Arbia, Gallo Nero, and Valle del
            Chianti. FuelCyclePro builds the plan and per-ride fueling for the distance you signed
            up for.
          </p>

          <div className="text-left space-y-6 sm:space-y-7 max-w-xl mx-auto">
            {raceDayGroups.map((group) => (
              <section key={group.raceDay} aria-labelledby={`eroica-${group.raceDay}`}>
                <h2
                  id={`eroica-${group.raceDay}`}
                  className="font-semibold text-stone-900 dark:text-white"
                >
                  {formatEroicaRaceDayLabel(group.raceDay)}
                </h2>
                <ul className="mt-3 space-y-3 sm:space-y-3.5">
                  {group.routes.map((route) => (
                    <li
                      key={route.id}
                      className="rounded-2xl bg-stone-100/80 dark:bg-stone-900/70 px-4 py-3.5 sm:px-5 sm:py-4 shadow-sm"
                    >
                      <p className="font-semibold text-stone-900 dark:text-white">
                        {route.shortName}
                        <span className="font-normal text-stone-500 dark:text-stone-400">
                          {' '}
                          - {route.kind} - {route.aidStations}{' '}
                          {route.aidStations === 1 ? 'aid station' : 'aid stations'}
                        </span>
                      </p>
                      <p className="mt-0.5 text-sm sm:text-base text-stone-600 dark:text-stone-300">
                        {formatDistance(route.distance)} km - {formatElevation(route.elevation)} -
                        Start {route.start}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <p className="text-xs sm:text-sm text-stone-500 dark:text-stone-500">
            Distances provisional per{' '}
            <a
              href={EROICA_OFFICIAL_ROUTES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-stone-800 dark:hover:text-stone-300"
            >
              eroica.cc
            </a>
          </p>

          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center pt-2">
            <Link
              to={registerHref}
              onClick={handleRegisterClick}
              className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5"
              data-cta="eroica-register"
            >
              Register free
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={signInHref}
              onClick={handleSignInClick}
              className="btn-chip inline-flex items-center justify-center gap-2 px-6 py-3.5"
              data-cta="eroica-sign-in"
            >
              Sign in
            </Link>
          </div>
          {showStores ? (
            <div className="space-y-3 pt-2">
              <p className="text-sm text-stone-500 dark:text-stone-400">
                Prefer the app - download for iPhone or Android.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
                {storeLinks.apple ? (
                  <a
                    href={storeLinks.apple}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleAppStoreClick}
                    className="inline-flex min-h-11 items-center justify-center rounded-xl bg-primary-500 px-5 py-3 font-semibold text-white transition-colors hover:bg-primary-600"
                    data-cta="eroica-app-store"
                  >
                    Download on the App Store
                  </a>
                ) : null}
                {storeLinks.play ? (
                  <a
                    href={storeLinks.play}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handlePlayStoreClick}
                    className="inline-flex min-h-11 items-center justify-center rounded-xl bg-primary-500 px-5 py-3 font-semibold text-white transition-colors hover:bg-primary-600"
                    data-cta="eroica-play-store"
                  >
                    Get it on Google Play
                  </a>
                ) : null}
              </div>
            </div>
          ) : null}
          <p className="text-xs sm:text-sm text-stone-500 dark:text-stone-500">
            Open registration - no invite code. Training and fueling matched to your Chianti
            distance.
          </p>
          <p className="text-xs sm:text-sm text-stone-500 dark:text-stone-500">
            FuelCyclePro is not affiliated with L'Eroica.
          </p>
        </div>
      </main>

      <footer className="border-t border-stone-200 dark:border-stone-800 py-6 text-center text-xs sm:text-sm text-stone-500">
        <p>
          &copy; {new Date().getFullYear()} FuelCyclePro by{' '}
          <a
            href="https://hotchillistudios.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-stone-900 dark:hover:text-white transition-colors"
          >
            Hot Chilli Studios
          </a>
        </p>
      </footer>
    </div>
  );
}
